import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PATH_TO_WARNING_AUDIO } from "shared/config/setting";
import { useRef } from "react";
import { Col, message, Layout, Row, Space, Switch, Collapse } from "antd";

import { LockOutlined, CheckOutlined } from "@ant-design/icons";
import GamepadController from "features/robot-teleop/components/joystick/JoystickController";
import TwistKeyboardMoveCommander from "features/robot-teleop/components/keyboard/TwistKeyboardMoveCommander";
import { useRobotPeerConnection } from "features/robot-teleop/contexts/RobotConnectionContext";
import RobotStatusViewer from "features/robot-teleop/components/robot-status/status-ctx/robot-status";
import VideoTrackPlayer from "features/robot-teleop/components/video-viewer/VideoTrackPlayer";
import JitterGraph from "features/robot-teleop/components/JitterGraph";
import "shared/assets/styles/components/demobot-control-page.css";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import LogViewer from "shared/components/ui/LogViewer";

const { Panel } = Collapse;

const DemobotControlPage = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const { id } = useParams();

  const {
    isOpen: signal_ready,
    message: signal_message,
    sendMessage: signal_send,
    subscribe,
    error: signal_Error,
    reconnectCountDown,
  } = useServerConnection();

  const audioAlarm = useRef(null);

  const [connectionLogRecords, setConnectionLogRecords] = useState([]);

  const {
    setRobotId,
    connectionState: rtcConnectionState,
    mediaTracks,
    channelSend,
    connect,
    // close: closePeerConnection,
    error,
    robotOnlineStatus,
    addChannelMessageReceivedEventListener,
  } = useRobotPeerConnection();

  const getVideoStream = (trackEvents, index = 0) => {
    console.log("get video stream: index=", index);
    if (trackEvents == null) return null;
    let tracks = trackEvents;
    if (!Array.isArray(tracks)) tracks = [tracks];
    let count = 0;
    for (let k in tracks) {
      const evt = tracks[k];
      if (evt.track.kind === "video") {
        if (count === index) {
          console.log(
            "found video stream ",
            index,
            " at ",
            count,
            "track",
            evt
          );
          return evt.streams[0];
        }
        count++;
      }
    }
    return null;
  };

  const getAudioStream = (trackEvents) => {
    if (trackEvents == null) return null;
    let tracks = trackEvents;
    if (!Array.isArray(tracks)) tracks = [tracks];
    for (let k in tracks) {
      const evt = tracks[k];
      if (evt.track.kind === "video") return evt.streams[0];
    }
    return null;
  };

  const frontCamaraIndex = 0;
  const rearCamaraIndex = 1;
  const videoStreamFront = getVideoStream(mediaTracks, frontCamaraIndex);
  const videoStreamRear = getVideoStream(mediaTracks, rearCamaraIndex);

  const audioStream = getAudioStream(mediaTracks);

  const [robotStatus, setRobotStatus] = useState(null);
  const [robotErrorInfo, setRobotErrorInfo] = useState(null);
  const [missionStatus, setMissionStatus] = useState(null);

  useEffect(() => {
    console.log("Robot id: ", id);
    setRobotId(id);
    const unlisten =
      addChannelMessageReceivedEventListener(handleChannelMessage);
    return () => {
      unlisten();
    };
  }, [id]);

  const handleChannelMessage = (channelMessage) => {
    if (!channelMessage) return;
    // log_debug("received message from robot: ", channelMessage);
    addLogRecord("received message from robot: " + channelMessage);
    if (channelMessage.channel == "chat") {
      const data = JSON.parse(channelMessage.data);
      if ("topic" in data) {
        const topic = data.topic;
        const topic_data = data.value;
        if (topic == "/battery") {
          const battery = {
            battery_level:
              topic_data.percentage != "undefined"
                ? Math.round(topic_data.percentage * 100)
                : null,
            now_charging: topic_data.power_supply_status == 1,
          };
          const robotInfo = { ...robotStatus, battery: battery };
          setRobotStatus(robotInfo);
        }
      } else {
        const msg_type = data["msg_type"];

        if (msg_type == "robot_state") {
          const msg_body = data["msg_body"];
          let robotInfo = msg_body["common_info"];
          const errorInfo = msg_body["error_info"];
          const missionInfo = msg_body["mission_info"];
          robotInfo["error_info"] = errorInfo;
          const connectionInfo = msg_body["connection_info"];
          if (connectionInfo != null) {
            robotInfo["connection_info"] = msg_body["connection_info"];
          }
          // console.log("robotInfo: ", robotInfo);
          // TODO: check map

          setRobotErrorInfo(errorInfo);
          setRobotStatus(robotInfo);
          setMissionStatus(missionInfo);
        }
      }
    }
  };

  const playAlarm = (play) => {
    const audio = audioAlarm.current;
    if (play) audio.play();
    else audio.pause();
  };

  useEffect(() => {
    if (!robotErrorInfo) playAlarm(false);
    else playAlarm(true);
  }, [robotErrorInfo]);

  const [isKeyboardControl, setKeyboardControl] = useState(false);

  const handleOnCommandCreated = (command, quite = false) => {
    console.log("Created command: ", command);
    if (channelSend == null) {
      alert("data channel is null!");
      return;
    }

    const topic_msg = { topic: "cmd_vel", value: command };
    let cmd_message = JSON.stringify(topic_msg);

    const channelLabel = "chat";

    channelSend(channelLabel, cmd_message);

    console.log("Quite: ", quite);
    if (!quite) {
      messageApi.open({
        type: "info",
        content: "Command has been sent to robot!",
        style: {
          marginTop: "10vh",
        },
      });
    }

    console.log("command has been sent:", cmd_message);
    addLogRecord("command has been sent:" + cmd_message);
  };

  const robotInfo = { robot_id: id, icon: "/images/demobot2.png", name: id };
  const connectionsStatus = {
    ServerConnectionStatus: signal_ready ? "connected" : "disconnected",
    PeerConnectionStatus: rtcConnectionState,
    RobotOnlineStatus: robotOnlineStatus,
  };

  const handleOnJoystickCommandCreated = (joyMsg, quite = false) => {
    console.log("sending joy to robot: ", joyMsg);
    if (channelSend == null || rtcConnectionState != "connected") {
      messageApi.open({
        type: "error",
        content: "Connection is not ready!",
        style: {
          marginTop: "10vh",
        },
      });
      return;
    }

    const topic_msg = { topic: "/joy", value: joyMsg };
    let cmd_message = JSON.stringify(topic_msg);

    const channelLabel = "chat";

    channelSend(channelLabel, cmd_message);

    console.log("Quite: ", quite);
    if (!quite) {
      messageApi.open({
        type: "info",
        content: "Command has been sent to robot!",
        style: {
          marginTop: "10vh",
        },
      });
    }

    console.log("command send:", cmd_message);
    addLogRecord("joystick sent:" + cmd_message);
  };

  const addLogRecord = (msg) => {
    console.log("debug, log msg: ", msg);
    const time = new Date();
    const logRecord = { Time: time, Message: msg };
    setConnectionLogRecords((prev) => {
      return [...prev, logRecord];
    });
  };

  return (
    <div className="robot-control-body">
      <div className="status-panel">
        {contextHolder}
        <RobotStatusViewer
          RobotStatus={robotStatus}
          RobotInfo={robotInfo}
          MissionInfo={missionStatus}
        ></RobotStatusViewer>
        <audio
          ref={audioAlarm}
          id="alarm-sound"
          src={PATH_TO_WARNING_AUDIO}
          loop
        ></audio>
      </div>

      <div className="jitter-panel">
        <JitterGraph></JitterGraph>
      </div>

      <div className="cameras-panel">
        <div className="camera">
          <VideoTrackPlayer
            className="front-camera"
            // mediaTracks={mediaTracks}
            title="Front Camera"
            videoMediaStream={videoStreamFront}
            audioMediaStream={audioStream}
            // ShowDelayTime={true}
            CameraStyle={{ height: "100%", maxHeight: "65vh" }}
          ></VideoTrackPlayer>
        </div>
        <div className="camera">
          <VideoTrackPlayer
            className="front-camera"
            // mediaTracks={mediaTracks}
            title="Front Camera"
            videoMediaStream={videoStreamRear}
            audioMediaStream={audioStream}
            // ShowDelayTime={true}
            CameraStyle={{ height: "100%", maxHeight: "65vh" }}
          ></VideoTrackPlayer>
        </div>
      </div>
      <div className="control-panel">
        Keyboard/Joystick:
        <Switch
          onChange={(checked) => {
            setKeyboardControl(checked);
          }}
          unCheckedChildren={<LockOutlined />}
          checkedChildren={<CheckOutlined />}
          defaultChecked
          checked={isKeyboardControl}
        />
        {!isKeyboardControl && (
          <GamepadController
            // disabled={rtcConnectionState != "connected"}
            onGamepadStateUpdated={(joyMsg) => {
              handleOnJoystickCommandCreated(joyMsg, true);
            }}
            showVirtualJoystick={true}
          />
        )}
        {isKeyboardControl && (
          <TwistKeyboardMoveCommander
            EnableKeyboard={true}
            //   visible={isRobotControlEnabled}
            //   onMoveCommand={handleMoveCommand}
            OnCommandCreated={handleOnCommandCreated}
          ></TwistKeyboardMoveCommander>
        )}
      </div>
      <div className="log-panel">
        Log messages:
        <LogViewer
          LogRecords={connectionLogRecords}
          displaySize={5}
        ></LogViewer>
      </div>
    </div>
  );
};

export default DemobotControlPage;
